// AIDEV-NOTE: Pure utility functions for DevDocs file browser — no React dependencies.

export interface DocFile {
  path: string;
  content: string;
}

export interface FileTreeSection {
  label: string;
  files: DocFile[];
  subdirectories: { label: string; files: DocFile[] }[];
}

/**
 * Strips .md extension, replaces - and _ with spaces, and title-cases the result.
 * Used for section/subdirectory labels that don't have file content.
 */
export const humanizeFilename = (path: string): string => {
  const filename = path.split('/').pop() || path;
  return filename
    .replace(/\.md$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Extracts label from a file's content. If the first line is a markdown
 * heading (e.g. "# Some Title"), returns that title. Otherwise falls back
 * to humanizeFilename.
 */
export const getFileLabel = (file: DocFile): string => {
  const firstLine = file.content.split('\n', 1)[0];
  const match = firstLine.match(/^#\s+(.+)/);
  return match ? match[1].trim() : humanizeFilename(file.path);
};

/**
 * Groups a flat file list into sections by top-level directory,
 * with nested subdirectories (e.g., tickets/test-coverage/) as sub-groups.
 */
export const buildFileTree = (files: DocFile[]): { rootFiles: DocFile[]; sections: FileTreeSection[] } => {
  const rootFiles: DocFile[] = [];
  const sectionMap: Record<string, { files: DocFile[]; subdirs: Record<string, DocFile[]> }> = {};

  files.forEach((file) => {
    const parts = file.path.split('/');

    if (parts.length === 1) {
      rootFiles.push(file);
      return;
    }

    const section = parts[0];
    if (!sectionMap[section]) {
      sectionMap[section] = { files: [], subdirs: {} };
    }

    if (parts.length === 2) {
      sectionMap[section].files.push(file);
    } else {
      const subdir = parts.slice(1, -1).join('/');
      if (!sectionMap[section].subdirs[subdir]) {
        sectionMap[section].subdirs[subdir] = [];
      }
      sectionMap[section].subdirs[subdir].push(file);
    }
  });

  const sectionOrder = ['docs', 'commands', 'skills', 'tickets'];
  const sections: FileTreeSection[] = sectionOrder
    .filter(key => sectionMap[key])
    .map(key => ({
      files: sectionMap[key].files,
      label: humanizeFilename(key),
      subdirectories: Object.entries(sectionMap[key].subdirs)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dirName, dirFiles]) => ({
          files: dirFiles,
          label: humanizeFilename(dirName),
        })),
    }));

  return { rootFiles, sections };
};
